import React from 'react';

import { dggTheme } from 'src/styles/dggTheme';
import type { HrefsByClient, SectionContent } from '../../../../content/dontgiftguide/types';
import { LevelTwoContentType } from '../../../../content/dontgiftguide/types';
import { getHrefForClient } from '../../../../utils/getHrefForClient';
import { getSlug } from '../../../../utils/getSlug';
import { parseCopy } from '../../../../utils/parseCopy';
import { ButtonLink } from '../../atoms/ButtonLink/ButtonLink';
import { Content } from '../../molecules/Content/Content';
import type { FeatureSectionProps } from '../../sectionLayouts/FeatureSection/FeatureSection';
import { FeatureSection } from '../../sectionLayouts/FeatureSection/FeatureSection';
import type { ItemsSectionProps } from '../../sectionLayouts/ItemsSection/ItemsSection';
import { ItemsSection } from '../../sectionLayouts/ItemsSection/ItemsSection';
import { MostMisGiftedSection } from '../../sectionLayouts/MostMisGiftedSection/MostMisGiftedSection';
import type { PartnerSectionProps } from '../../sectionLayouts/PartnerSection/PartnerSection';
import { PartnerSection } from '../../sectionLayouts/PartnerSection/PartnerSection';
import { TopGiftingTipsSection } from '../../sectionLayouts/TopGiftingTipsSection/TopGiftingTipsSection';
import * as styles from './SectionGroup.styles';

interface FooterLinks {
  href: HrefsByClient;
  text: string;
}

interface SectionGroupProps {
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  clientIsNativeApp: boolean;
  heading: string;
  copy: string;
  categoryHref?: HrefsByClient;
  darkTitleColor?: boolean;
  sections: SectionContent[];
  footer?: {
    title: string;
    links: FooterLinks[];
  };
}

export const SectionGroup = (props: SectionGroupProps) => {
  const {
    onLinkClick,
    clientIsNativeApp,
    heading,
    copy,
    categoryHref,
    sections,
    footer,
    darkTitleColor = false,
  } = props;
  const { title, links } = footer || {};
  const parsedCopy = parseCopy(copy);
  const customStyleID = getSlug(heading);

  return (
    <div css={styles.group}>
      <div css={styles.sections}>
        <Content darkCopyColor={darkTitleColor}>
          <div css={styles.headingContainer} data-custom-style-id={customStyleID}>
            <h2 css={styles.heading}>{heading}</h2>
            {parsedCopy && <p css={styles.copy}>{parsedCopy}</p>}
            {categoryHref && (
              <a
                css={styles.categoryLink}
                href={getHrefForClient(categoryHref, clientIsNativeApp)}
                onClick={onLinkClick}
              >
                Shop the category »
              </a>
            )}
          </div>
        </Content>

        {sections.map((section, index) => {
          const { contentType, ...sectionProps } = section;

          switch (contentType) {
            case LevelTwoContentType.Feature:
              return (
                <FeatureSection
                  key={index}
                  {...(sectionProps as FeatureSectionProps)}
                  onLinkClick={onLinkClick}
                />
              );
            case LevelTwoContentType.Items:
              return (
                <ItemsSection
                  key={index}
                  {...(sectionProps as ItemsSectionProps)}
                  onLinkClick={onLinkClick}
                />
              );
            case LevelTwoContentType.MostMisGifted:
              return (
                <MostMisGiftedSection
                  key={index}
                  showCategoryLinks={!clientIsNativeApp}
                  onLinkClick={onLinkClick}
                />
              );
            case LevelTwoContentType.Partner:
              return (
                <PartnerSection
                  key={index}
                  {...(sectionProps as PartnerSectionProps)}
                  onLinkClick={onLinkClick}
                />
              );
            case LevelTwoContentType.TopGiftingTips:
              return <TopGiftingTipsSection key={index} />;
            default:
              return null;
          }
        })}
      </div>
      {footer && (
        <Content backgroundColor={dggTheme.colors.moodyBlue}>
          <div css={styles.footer}>
            <h3 css={styles.footerTitle}>{title}</h3>
            {links &&
              links.map((link) => {
                const { text, href: hrefs } = link;
                const href = getHrefForClient(hrefs, clientIsNativeApp);

                return (
                  <ButtonLink
                    key={`buttonLink-${text}`}
                    text={text}
                    href={href}
                    onClick={onLinkClick}
                  />
                );
              })}
          </div>
        </Content>
      )}
    </div>
  );
};
